import { useState, useEffect, useRef } from 'react';
import * as ReactBootStrap from 'react-bootstrap';
import Skeleton from '@mui/material/Skeleton';
import Pagination from '@mui/material/Pagination';
import { visuallyHidden } from "@mui/utils";
import Stack from '@mui/material/Stack';
import { ReactComponent as AscIcon } from "../../svgs/asc-sort.svg";
import sleep from '../functions/sleep'; 
import { Box, PaginationItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const ApiDataTable = ({ RowComponent, tableHeader, dataList, numOfPages, numToPage, totalDatalistLength, showSpinner, setShowSpinner, skeletonHeight, onPagination, pageParams,stickyFirstColumnEnabled }) => {
    // initialize state
    const [tableCss, setTableCss] = useState('table');
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState(1);
    const [order, setOrder] = useState(null);
    const [orderBy, setOrderBy] = useState(null);
    const [loading, setLoading] = useState(false); 
    const [sortClicks, setSortClicks] = useState(0);

    // initialize ref
    const count = useRef(0);
    const pageNum = useRef(1); 
    const initialRender = useRef(true); 
    // function to make count equal to 0
    const zeroCount = () =>{
        count.current = 0;
    }
    // increment count by 1
    const incrementCount = () =>{
        count.current = count.current + 1;
    }

    // Function to calculate pagination start and end values
    function calculatePagination(totalItems, itemsPerPage, currentPage) {
      const start = (currentPage - 1) * itemsPerPage + 1;
      let end = currentPage * itemsPerPage;
      if (end > totalItems) {
          end = totalItems;
      }
      return { start, end };
    }

    // make request on page change using function from parent
    // to process the page requests in order use GETQ in onPagination function 
    const onPageChange = (event, value) => {
        setPage(page => page = parseInt(value));
        setPageInput(parseInt(value));
        pageNum.current = parseInt(value); 
    }
    
    // Handle page input change - only allow numbers
    const handlePageInputChange = (event) => {
        const value = event.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            const numValue = parseInt(value) || '';
            setPageInput(numValue);
        }
    };
    
    // Handle Enter key press on page input
    const handlePageInputKeyPress = (event) => {
        if (event.key === 'Enter') {
            // Return early if pageInput is empty or not a number
            if (!pageInput || isNaN(pageInput)) {
                // Reset to current page
                setPageInput(page);
                return;
            }
    
            let newPage = parseInt(pageInput);
            if (newPage < 1) {
                newPage = 1;
                setPageInput(1);
            } else if (newPage > numOfPages) {
                newPage = numOfPages;
                setPageInput(numOfPages);
            }
            setPage(newPage);
            pageNum.current = newPage;
        }
    };
    
    // Handle blur event on page input
    const handlePageInputBlur = () => {
        if (!pageInput || isNaN(pageInput)) {
            setPageInput(page);
        } else {
            // Check if the input is out of range and adjust it
            let newPage = parseInt(pageInput);
            if (newPage < 1) {
                setPageInput(1);
            } else if (newPage > numOfPages) {
                setPageInput(numOfPages);
            }
        }
    };

    const createSortHandler = (property) => (event) => {
      handleRequestSort(event, property);
    };

    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      const newOrder = isAsc ? "desc" : "asc";
      const newSortClicks = orderBy === property ? sortClicks + 1 : 1;
      if (newSortClicks === 3) {
        setOrderBy(null);
        setOrder(null);
        setSortClicks(0);
        onPagination(pageNum.current); 
      } else {
        setOrder(newOrder);
        setOrderBy(property);
        setSortClicks(newSortClicks);
        onPagination(pageNum.current,property,newOrder); 
      }
      
  };

    // useEffect to make request new page data 
    // wait until group of page changes stops to make request
    useEffect(() => {
        if (initialRender.current){
            initialRender.current = false; 
        }
        else {
            // set loading to show skeleton
            setLoading(loading => loading = true); 
            // wait to see if page continues to change
            sleep(300).then(res => {
                // check that page has not changed to make request
                // (state will remain the same but ref will change if pagination)
                console.log(pageNum.current + ' == ' + page); 
                if (pageNum.current == page){
                    return onPagination(pageNum.current,orderBy,order); 
                }
                // return empty object if page changed
                else {
                    return; 
                }
            }).then(res => {
                console.log(res); 
                console.log(res + ' && ' + pageNum.current + ' == ' + page); 
                // only hide skeleton if it is request for current page
                if (res && pageNum.current == page) {
                    // set loading to false to hide skeleton
                    setLoading(loading => loading = false); 
                }
            }).catch(err => {
                console.log(err); 
                // set loading to false to hide skeleton
                setLoading(loading => loading = false); 
            }); 
        }
    }, [page]); 

    // set page to 1 and reset sort on pageParams change
    useEffect(() => {
        setPage(page => page = 1);
        setPageInput(1);
        setOrderBy(null);
        setOrder(null);
        setSortClicks(0);
        pageNum.current = 1; 
    }, [pageParams.current]);

    return (
      <>
        {!showSpinner && !loading ? (
          <>
            <Paper sx={{borderRadius:"12px"}}>
              <TableContainer sx={{ maxHeight: "calc(100vh - 465px)", overflow: "overlay" }}>
                <Table stickyHeader size="small">
                  {/* display headers */}
                  <TableHead className="data-table-header">
                    <TableRow>
                      {tableHeader.map((el) => (
                        <TableCell
                          className={stickyFirstColumnEnabled && el['columnorder']===0? "table-cell-sticky-header": ""}
                          sx={{
                            // backgroundColor:"#CCDBE3",
                            backgroundColor: process.env.REACT_APP_PRIMARY_COLOR, 
                            color:`${process.env.REACT_APP_HEADER_TEXT_COLOR} !important`,
                            borderRight: "1px solid white",
                          }}
                          key={el["columnname"]}
                          sortDirection={
                            orderBy === el["columnname"] ? order : false
                          }
                        >
                          <TableSortLabel
                            active={orderBy === el["columnname"]}
                            direction={
                              orderBy === el["columnname"] ? order : "asc"
                            }
                            onClick={createSortHandler(el["columnname"])}
                            IconComponent={AscIcon}
                            sx={{
                              '&.Mui-active': {
                                color: 'white',
                              }
                            }}
                          >
                            {el["columntag"]}
                            {orderBy === el["columnname"] ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  {/* display body of table */}
                  <TableBody className="data-table-body">
                    {/* zero count at the start of each row */}
                    {zeroCount()}
                    {dataList.map((row,index) => (
                      <TableRow
                        sx={{
                          backgroundColor:
                            index % 2 !== 0 ? "#F7F9FA" : "#FFFFFF",
                          }}>
                        {/* apply row component function to each row object */}
                        {/* pass table headers to row component so order can be held */}
                        {RowComponent(row, count.current, tableHeader,stickyFirstColumnEnabled)}
                        {/* increment count for each element in row */}
                        {incrementCount()}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* if specified number of rows to page and there is more than one page show pagination component */}
              {!showSpinner &&
              dataList.length == 0 &&
              tableHeader.length > 0 ? (
                <div className="d-flex flex-column justify-content-end align-items-center p-2">
                <ErrorOutlineIcon sx={{fontSize:"40px"}}/>
                <Typography>No Data Available.</Typography>
              </div>
              ) : (
                numOfPages > 1 ? (
                  <div className="d-flex justify-content-end align-items-center p-2">
                    <Typography
                      fontSize={"12px"}
                      paddingRight={"8px"}
                      fontWeight={"400"}
                      lineHeight={"16px"}
                    >
                      {calculatePagination(totalDatalistLength, numToPage, page).start +
                        "-" +
                        calculatePagination(totalDatalistLength, numToPage, page).end +
                        " of " +
                        totalDatalistLength +
                        " items"}
                    </Typography>
                    <Pagination
                      page={page}
                      count={numOfPages}
                      variant="outlined"
                      shape="rounded"
                      onChange={onPageChange}
                      renderItem={(item) => (
                        <PaginationItem
                          {...item}
                          components={{
                            previous: () => (
                              <Typography fontSize={"12px"}>Previous</Typography>
                            ),
                            next: () => (
                              <Typography fontSize={"12px"}>Next</Typography>
                            ),
                          }}
                        />
                      )}
                    />
                    <Box className="d-flex align-items-center ms-2 me-1">
                      <Typography fontSize={"12px"} className="me-2">
                        Page
                      </Typography>
                      <TextField
                        size="small"
                        value={pageInput}
                        onChange={handlePageInputChange}
                        onKeyPress={handlePageInputKeyPress}
                        onBlur={handlePageInputBlur}
                        inputProps={{
                          style: { 
                            padding: '7px 6px',
                            width: '50px',
                            textAlign: 'center',
                            fontSize: '12px'
                          },
                          'aria-label': 'Jump to page'
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 1,
                            borderColor: '#00A6CA',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00A6CA'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#00A6CA'
                            }
                          },
                          '& .MuiOutlinedInput-input': {
                            color: '#00A6CA',
                            '&:hover': {
                              color: '#00A6CA'
                            }
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00A6CA'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00A6CA'
                          },
                          '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#BDBDBD'
                          },
                          '& .Mui-disabled input': {
                            color: '#BDBDBD',
                            backgroundColor: '#FCFCFC'
                          }
                        }}
                      />
                      <Typography fontSize={"12px"} sx={{ ml: 1 }}>
                        of {numOfPages}
                      </Typography>
                    </Box>
                  </div>
                ) : (
                  <></>
                )
              )}
            </Paper>
          </>
        ) : (
          <div style={{ maxHeight: "calc(100vh - 465px)", overflow: "overlay" }}>
            {/* show skeleton while showSpinner prop is true */}
            {/* generally used for waiting for api response */}
            <Skeleton
              variant="rectangular"
              width="100%"
              height="7vh"
              sx={{ borderRadius: "10px", "margin-bottom": "3px" }}
            />
            <Skeleton
              variant="rectangular"
              width="100%"
              height={skeletonHeight}
              sx={{ borderRadius: "10px" }}
            />
            <br />
          </div>
        )}
      </>
    );
}

export default ApiDataTable